import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { get, put } from "../lib/api";
import { usePermission } from "../hooks/useSession";
import type { CollectionRef, TagRef } from "../lib/types";
import { TagInput } from "./TagInput";
import { CollectionPicker } from "./CollectionPicker";
import { Panel } from "./ui";

export function DocumentOrganize({
  documentId,
  tags,
  collections,
}: {
  documentId: string;
  tags: readonly TagRef[];
  collections: readonly CollectionRef[];
}) {
  const client = useQueryClient();
  const can = usePermission();
  const canWrite = can("documents:write");
  const [tagNames, setTagNames] = useState(() => tags.map((tag) => tag.name));
  const [collectionIds, setCollectionIds] = useState(() => collections.map((c) => c.id));

  const allTags = useQuery({
    queryKey: ["tags"],
    queryFn: () => get<{ tags: TagRef[] }>("/v1/tags"),
  });
  const allCollections = useQuery({
    queryKey: ["collections"],
    queryFn: () => get<{ collections: CollectionRef[] }>("/v1/collections"),
  });

  const saveTags = useMutation({
    mutationFn: (next: string[]) => put<{ tags: TagRef[] }>(`/v1/documents/${documentId}/tags`, { tags: next }),
    // Replacing the whole set makes a retry harmless, so no optimistic bookkeeping is
    // needed — and a correction resolves the flag that named its field, so this
    // invalidates rows, exceptions and review together.
    onSuccess: (response) => {
      setTagNames(response.tags.map((tag) => tag.name));
      void client.invalidateQueries();
    },
  });
  const saveCollections = useMutation({
    mutationFn: (next: string[]) =>
      put<{ collections: CollectionRef[] }>(`/v1/documents/${documentId}/collections`, { collections: next }),
    onSuccess: (response) => {
      setCollectionIds(response.collections.map((c) => c.id));
      void client.invalidateQueries();
    },
  });

  if (!canWrite) {
    return null;
  }

  return (
    <Panel title="organize">
      <div className="flex flex-col gap-4 px-4 py-4">
        <label className="flex flex-col gap-2">
          <span className="eyebrow">tags</span>
          <TagInput
            value={tagNames}
            suggestions={(allTags.data?.tags ?? []).map((tag) => tag.name)}
            onChange={(next) => {
              setTagNames(next);
              saveTags.mutate(next);
            }}
          />
        </label>
        <label className="flex flex-col gap-2">
          <span className="eyebrow">collections</span>
          <CollectionPicker
            value={collectionIds}
            options={allCollections.data?.collections ?? []}
            onChange={(next) => {
              setCollectionIds(next);
              saveCollections.mutate(next);
            }}
          />
        </label>
        {(saveTags.isError || saveCollections.isError) && (
          <p className="font-mono text-[11px] text-crit">
            &gt; could not save — try again
          </p>
        )}
      </div>
    </Panel>
  );
}
